/**
 * The Finance Kanban's docked record (2026-10-14): ONE CARD'S HANDOFF, then the resident for context.
 *
 * Why this exists: the resident dossier is keyed by RESIDENT, and the handoff it acts on is the resident's default
 * pick ("still being worked first, else the latest"; financeContextService). A card is one handoff -- a closed or
 * returned one, or one of two open handoffs of the same resident -- so a dossier opened from a card could act on a
 * DIFFERENT handoff. This panel never does:
 *
 *   - it reads the dossier naming the card's handoff (GET /finance/residents/:id?handoff=<the card's handoff_id>);
 *   - it acts only when what came back IS the card's: the same resident, the same handoff id, and (UMED) the same
 *     obligation (F6). Otherwise it offers nothing;
 *   - it hands HandoffWork a context whose `handoff` is that record, so every act posts to the card's handoff id,
 *     with the version this screen shows (409 STALE re-reads), one Idempotency-Key per opening, the reasons, the R1
 *     Monday-reference notice and the acts' own preconditions -- HandoffWork's forms, unchanged;
 *   - Receive (a card Finance has not received) names the card's UMED obligation; the send-back names the card's
 *     handoff and keeps its own dialog (acceptor only, authenticator code, reason; the server decides);
 *   - the dossier beneath is the same context with its own handoff acts hidden, so there is one set of acts;
 *   - ATD (2026-10-15): the ATD panel is drawn HERE, for card.residentId -- never the dossier's pick -- with its one
 *     primary "Mark ATD" (and the dossier beneath draws none). The board's "Mark ATD…" act opens its confirmation.
 *
 * What it says is the record it READ, never the card it was opened from (F2): after an act the card may be stale or
 * off the loaded pages, so the heading, the Receive gate and the acts all come from the fresh read; after Receive the
 * panel follows the new handoff by the id the act returned.
 *
 * A drop or "Move…" reaches here as a `request`: taken once (onConsumed), the record is READ AGAIN (F5), and the act
 * is resolved against that read (resolveBoardAct). At most it OPENS a drawer or a dialog; nothing is recorded until
 * its own confirm is pressed.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  financeApi, outcomeOf, freshKey, FinanceScope, ResidentContext, PRIVATE_PAY_LABEL,
} from './financeApi';
import HandoffWork, { easternDay, UNSURE_MESSAGE } from './HandoffWork';
import type { HandoffWorkRequest } from './HandoffWork';
import { Context } from './ResidentDossier';
import SendBackDialog from '../../components/sendBack/SendBackDialog';
import AtdPanel from '../../components/atd/AtdPanel';
import { sendBackPath } from '../../components/sendBack/sendBackApi';
import { resolveBoardAct, STATE_WORD, returnedWord } from './financeKanbanModel';
import type { FinanceCard, FinanceBoardAct } from './financeKanbanModel';

export interface CardActRequest { cardId: string; act: FinanceBoardAct; n: number }

type HandoffRecord = NonNullable<ResidentContext['handoff']>;

/** No answer, or a gateway's, is not a refusal: the write may have committed (HandoffWork's rule). */
const unsure = (status: number | undefined) => status === undefined || status === 502 || status === 503 || status === 504;

/**
 * The card's own handoff in a read, or null: the same resident, the same handoff id and, for a UMED handoff, the same
 * obligation. Anything else -- the resident's default pick, another resident -- is never acted on.
 */
export function recordFor(ctx: ResidentContext | null, card: FinanceCard, handoffId: string | null): HandoffRecord | null {
  if (!ctx || !handoffId || !ctx.handoff) return null;
  if (ctx.resident.id !== card.residentId) return null;
  const h = ctx.handoff;
  if (h.id !== handoffId) return null;
  if (card.umedActionId && (h.umed_action_id || null) !== card.umedActionId) return null;
  return h;
}

export default function CardActPanel({ card, scope, request = null, onConsumed, onChanged }: {
  card: FinanceCard;
  scope: FinanceScope;
  /** A drop or "Move…" for this card: what to open once the handoff is read again. */
  request?: CardActRequest | null;
  /** The request was taken: the board forgets it, so nothing opens again by itself (F1). */
  onConsumed?: (n: number) => void;
  /** Something was recorded (or a re-read is due): the board reads its columns again. '' = no message. */
  onChanged: (msg: string) => void;
}) {
  const [ctx, setCtx] = useState<ResidentContext | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [workReq, setWorkReq] = useState<HandoffWorkRequest | null>(null);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const sentBack = useRef(false);
  const canManage = Boolean(scope.can.manage_handoff);
  const canSendBack = Boolean(scope.can.send_back);

  // The handoff this panel follows: the card's, or the one Receive just made (F2).
  const [handoffId, setHandoffId] = useState<string | null>(card.handoffId);
  useEffect(() => { if (card.handoffId) setHandoffId(card.handoffId); }, [card.handoffId]);

  /*
   * "Changed since the board was read": the record's version against the version this panel last had a right to
   * expect -- the card's, until this panel's own act, then the record it read back (F2: never a flag that stays set).
   */
  const [expected, setExpected] = useState<number | null>(card.version);
  useEffect(() => { setExpected(card.version); }, [card.version]);
  /* ── Read the card's handoff (and the resident around it) ─────────── */
  const want = useRef(handoffId);
  want.current = handoffId;
  /** `adopt`: this read follows this panel's own act, so its version is the one now expected (set with the read, never after it). */
  const load = useCallback(async (adopt = false): Promise<ResidentContext | null> => {
    if (!card.residentId) return null;
    const asked = handoffId;
    setErr(null);
    try {
      const r = await financeApi.resident(card.residentId, { handoff: asked });
      if (want.current !== asked) return null;
      if (!r.success || !r.data) { setCtx(null); setErr(r.code === 'NOT_FOUND' ? 'No such resident here. They may be in a building outside your scope.' : outcomeOf(r)); return null; }
      if (adopt) { const rec = recordFor(r.data, card, asked); if (rec) setExpected(rec.version); }
      setCtx(r.data);
      return r.data;
    } catch {
      if (want.current === asked) { setCtx(null); setErr('Could not reach the server.'); }
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.residentId, handoffId]);
  useEffect(() => { load(); }, [load]);

  const record = recordFor(ctx, card, handoffId);
  const mismatch = Boolean(ctx && (ctx.resident.id !== card.residentId || (handoffId && !record)));
  const cardCtx: ResidentContext | null = ctx && !mismatch ? { ...ctx, handoff: record } : null;

  const stale = Boolean(record && expected !== null && record.version !== expected);
  const staleSeen = useRef<number | null>(null);
  useEffect(() => {
    if (!stale || !record || staleSeen.current === record.version) return;
    staleSeen.current = record.version;
    onChanged('');
  }, [stale, record, onChanged]);

  /* ── A drop or Move…: taken once, read again, then opened (never recorded) ── */
  const seen = useRef<number | null>(null);
  // Consuming the request clears it upstream; that must not cancel this read, only unmounting does.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const receiveBtn = useRef<HTMLButtonElement | null>(null);
  const [receiveAsked, setReceiveAsked] = useState(false);
  /** "Mark ATD…" from the board: the ATD panel below opens its confirmation for the card's resident. */
  const [atdAsk, setAtdAsk] = useState<number | null>(null);
  useEffect(() => {
    if (!request || request.cardId !== card.id || seen.current === request.n) return;
    seen.current = request.n;
    if (onConsumed) onConsumed(request.n);
    setSaid(null);
    // ATD is the resident's, not the handoff's: no handoff read decides it; the panel reads the resident itself.
    if (request.act === 'mark-atd') { setAtdAsk(request.n); return; }
    (async () => {
      const fresh = await load(); // F5: decide on a read made for this request, not an earlier one
      if (!mounted.current || !fresh) return;
      if (fresh.resident.id !== card.residentId || (handoffId && !recordFor(fresh, card, handoffId))) {
        setSaid('eMerge did not return this card\'s handoff on the resident\'s record, so nothing was opened.');
        return;
      }
      const rec = recordFor(fresh, card, handoffId);
      const r = resolveBoardAct(request.act, card, rec, { manage: canManage, sendBack: canSendBack });
      if (r.kind === 'work') setWorkReq({ acts: r.acts, n: request.n, payState: r.payState, prompt: r.prompt });
      else if (r.kind === 'receive') { setReceiveAsked(true); window.setTimeout(() => receiveBtn.current?.focus(), 0); }
      else if (r.kind === 'send-back') { sentBack.current = false; setSendBackOpen(true); }
      else setSaid(r.why);
    })();
  // The request's n decides; the rest is this render's.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  /* ── Receive: the card's UMED obligation, one key per opening, reused on a retry ── */
  const [receiving, setReceiving] = useState(false);
  const [receiveErr, setReceiveErr] = useState<string | null>(null);
  const receiveKey = useRef<string | null>(null);
  const receive = async () => {
    if (receiving || !card.umedActionId) return;
    setReceiving(true); setReceiveErr(null);
    if (!receiveKey.current) receiveKey.current = freshKey();
    try {
      const r = await financeApi.receiveHandoff(card.umedActionId, receiveKey.current);
      if (r.success && r.data) {
        receiveKey.current = null; setReceiveAsked(false);
        setNotice(r.data.replayed ? 'That handoff was already received.' : 'Handoff received. Accept it to hold it.');
        setExpected(r.data.version ?? null);
        setHandoffId(r.data.handoffId); // follow the handoff Receive made, whatever the card still says
        onChanged('Handoff received.');
      } else setReceiveErr(unsure(r.status) ? UNSURE_MESSAGE : outcomeOf(r, { wrote: true })); // F4
    } catch { setReceiveErr(UNSURE_MESSAGE); } finally { setReceiving(false); }
  };

  const changed = useCallback(async (msg: string) => {
    setNotice(msg || null);
    onChanged(msg);
    await load(true); // this panel's own act: the read-back is the new expectation
  }, [onChanged, load]);
  const reload = useCallback(async () => { await load(); onChanged(''); }, [load, onChanged]);

  if (!card.residentId) return <section className="um-card"><p className="um-quiet">This handoff names no resident, so there is nothing to open.</p></section>;

  const pp = card.kind === 'PRIVATE_PAY';
  // The state said is the record's (F2); a card with no handoff yet is "not received".
  /*
   * The state said is the record's, in the SERVER's word (N2: the dossier returns emerge_state from the board's own
   * CASE; nothing is derived here). A returned handoff says where it went. A server without emerge_state still says
   * the record's own state, never "reading…" once the record is read.
   */
  const recordWord = record
    ? (record.emerge_state && record.emerge_state.key === 'returned' ? returnedWord(card)
      : record.emerge_state ? record.emerge_state.word : String(record.state).replace(/_/g, ' ').toLowerCase())
    : null;
  const word = recordWord || (handoffId ? 'reading…' : STATE_WORD['not-received']);
  const handedOn = card.row.handoff && card.row.handoff.handed_at ? easternDay(card.row.handoff.handed_at) : null;
  const canReceive = canManage && !pp && !handoffId && Boolean(card.umedActionId);

  return (
    <>
      <section className="um-card fn-cardpanel" aria-label="This card's handoff">
        <div className="um-card-head">
          <div>
            <h2>{pp ? (card.row.handoff?.label || PRIVATE_PAY_LABEL) : 'UMED'} handoff · {word}</h2>
            <p className="why">
              Every act here is on this card&rsquo;s handoff only{handedOn ? `, handed to Finance ${handedOn}` : ''}, never on another of {card.name}&rsquo;s.
            </p>
          </div>
        </div>
        {notice && <p className="um-notice ok" role="status">{notice}</p>}
        {said && <p className="um-notice" role="status">{said}</p>}
        {err && <p className="um-notice stop" role="alert">{err} <button type="button" className="um-open" onClick={() => { load(); }}>Try again</button></p>}
        {!err && !ctx && <p className="um-quiet" role="status">Reading this handoff…</p>}
        {stale && record && (
          <p className="um-notice" role="note">
            This handoff changed since the board was read: it is {(recordWord || '').toLowerCase()} now. What is shown here is current; the board is being read again.
          </p>
        )}
        {mismatch && (
          <p className="um-notice stop" role="alert">
            eMerge did not return this card&rsquo;s handoff on {card.name}&rsquo;s record, so no act is offered here. Open it from &ldquo;Handed to Finance&rdquo;, or tell engineering.
          </p>
        )}

        {/* Not received yet: the only act is Receive, naming the card's UMED obligation. */}
        {cardCtx && !handoffId && (
          <div className="um-section">
            <p>UMED handed {card.name} to Finance{handedOn ? ` on ${handedOn}` : ''}. Finance has not received it in eMerge yet.</p>
            {receiveAsked && <p className="um-notice" role="note">Receive records that Finance has this handoff. Nothing is recorded until you press Receive.</p>}
            {canReceive && (
              <div className="fn-sendback">
                <button type="button" ref={receiveBtn} className={`um-open${receiveAsked ? ' um-primary' : ''}`} disabled={receiving} onClick={receive}>
                  {receiving ? 'Receiving…' : `Receive handoff for ${card.name}`}
                </button>
                {receiveErr && <p className="um-notice stop" role="alert">{receiveErr}</p>}
              </div>
            )}
          </div>
        )}

        {/* The card's handoff: HandoffWork's own forms and calls, on a context whose handoff IS this card's. */}
        {cardCtx && record && (
          <>
            {canSendBack && !pp && record.state === 'ACCEPTED' && (
              <div className="fn-sendback" style={{ padding: '0 16px' }}>
                <button type="button" className="um-open" aria-haspopup="dialog" onClick={() => { sentBack.current = false; setSendBackOpen(true); }}>Send back to UMED…</button>
              </div>
            )}
            <HandoffWork key={record.id} ctx={cardCtx} scope={scope} onChanged={changed} onReload={reload} request={workReq} />
            {!pp && (
              <SendBackDialog
                open={sendBackOpen} path={sendBackPath.finance(record.id)} recordLabel={card.name}
                onSent={() => { sentBack.current = true; }}
                onClose={() => { setSendBackOpen(false); if (sentBack.current) { sentBack.current = false; changed('Sent back to UMED.'); } }}
              />
            )}
          </>
        )}
      </section>
      {/* ATD for THIS card's resident (card.residentId), drawn once, above the resident's context. */}
      <AtdPanel patientId={card.residentId} openMark={atdAsk} onChanged={(msg) => onChanged(msg)} card />
      {cardCtx && <Context ctx={cardCtx} scope={scope} notice={null} onChanged={changed} onReload={reload} handoffActs={false} atd={false} />}
    </>
  );
}
